import React, { memo } from 'react';
import styled from 'styled-components';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  TooltipItem,
  Legend,
} from 'chart.js';
import { COLORS } from '@constants/styles';
import GraphTitle from '../GraphTitle';

const ST = {
  Wrapper: styled.div<{ height: number }>`
    height: ${p => p.height}px;
    width: 100%;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  `,
  Container: styled.div`
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
  `,
  ChartWrapper: styled.div`
    width: 100%;
    display: flex;
    flex: 1;
    overflow: hidden;
  `,
  ChartContainer: styled.div`
    height: 100%;
    width: 100%;
    position: relative;
  `,
};

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

interface Props {
  xLabels: Array<string | number | Date>;
  data: Array<{
    label: string;
    data: Array<number> | Array<{ [key: string]: number | string }>;
    color?: string;
  }>;
  stacked: boolean;
  title?: string;
  subtitle?: string;
  height?: number;
  xAxisTitle?: string;
  yAxisTitle?: string;
  hideLegend?: boolean;
  tooltipTitleCallback?: (context: Array<TooltipItem<'bar'>>) => void;
  tooltipLabelCallback?: (context: TooltipItem<'bar'>) => void;
  tooltipAfterBodyCallback?: (context: Array<TooltipItem<'bar'>>) => void;
  tooltipFilterCallback?: (context: TooltipItem<'bar'>) => void;
  loadImage?: boolean;
}

const BarGraph: React.FC<Props> = ({
  xLabels,
  data,
  stacked,
  title = '',
  subtitle = '',
  height = 400,
  xAxisTitle = undefined,
  yAxisTitle = undefined,
  hideLegend = false,
  tooltipTitleCallback = () => {},
  tooltipLabelCallback = () => {},
  tooltipAfterBodyCallback = () => {},
  tooltipFilterCallback = () => {},
  loadImage = false,
}) => {
  const chartData = {
    labels: xLabels,
    datasets: data.map((d, i) => ({
      label: d.label,
      data: d.data,
      barPercentage: 1.0,
      backgroundColor: d.color || COLORS.RANGE[i],
    })),
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    ...(loadImage
      ? {
          devicePixelRatio: 4,
        }
      : {}),
    scales: {
      x: {
        stacked,
        title: {
          display: xAxisTitle !== undefined,
          text: xAxisTitle,
          color: COLORS.BLACK,
        },
        ticks: { color: COLORS.BLACK },
      },
      y: {
        stacked,
        title: {
          display: yAxisTitle !== undefined,
          text: yAxisTitle,
          color: COLORS.BLACK,
        },
        ticks: { color: COLORS.BLACK },
        beginAtZero: true,
      },
    },
    plugins: {
      legend: {
        display: !hideLegend,
        position: 'bottom',
      },
      tooltip: {
        callbacks: {
          title: tooltipTitleCallback,
          label: tooltipLabelCallback,
          afterBody: tooltipAfterBodyCallback,
        },
        filter: tooltipFilterCallback,
      },
    },
  };

  return (
    <ST.Wrapper height={height}>
      <ST.Container>
        {(title !== '' || subtitle !== '') && (
          <GraphTitle title={title} subtitle={subtitle} />
        )}
        <ST.ChartWrapper>
          <ST.ChartContainer>
            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
            {/* @ts-ignore */}
            <Bar options={chartOptions} data={chartData} />
          </ST.ChartContainer>
        </ST.ChartWrapper>
      </ST.Container>
    </ST.Wrapper>
  );
};

export default memo(BarGraph);
